import { ed25519 } from '@noble/curves/ed25519.js';
import { deflateSync, inflateSync } from 'fflate';
import { canonicalize, CANON_V2 } from './canon.ts';
import { concatBytes, fromUtf8, toBase64Url, utf8 } from './bytes.ts';

/** The y-canon-v2 document y.dk registers (backend-y-new document.ts, ProvenanceDocumentV2). */
export type CanonicalDocument = {
  title: string;
  body: string;
  published_at: string;
  url: string | null;
};

/**
 * NetID packet (SPEC.md §2). v2 adds `company` (the issuer's Concordium
 * company account), `page` (the URL of the page that shows the code) and
 * `customer` (the NetID customer id, which is then also the `issuer`), and
 * the kind `page`: a live code for a page with no article in it. A verifier
 * still accepts v1.
 */
export type NetIdPacket = {
  netid: 1 | 2 | 3;
  issuer: string;
  kid: string;
  kind: 'article' | 'video' | 'audio' | 'page';
  iat: number;
  nonce: string;
  /** Article kinds only. */
  uid?: string;
  version?: number;
  canon?: typeof CANON_V2;
  doc?: CanonicalDocument;
  sha256?: string;
  chain?: { network: 'mainnet' | 'testnet'; tx_hash: string };
  /** v2: the company account the issuer's binding is registered from. */
  company?: string;
  /** v2: the https URL of the page the code is shown on. */
  page?: string;
  /** v2: the NetID customer id (equals `issuer`). */
  customer?: string;
  /**
   * v3: the page view's stream session (SPEC.md §5.2, relay protection layer 2): `id` is 16 random
   * bytes, `pub` the session's own Ed25519 public key (32 bytes) that signs the per-frame ticks.
   * Both base64url. The packet signature covers them.
   */
  session?: PacketSession;
};

export type PacketSession = { id: string; pub: string };

export type ArticlePacket = NetIdPacket & Required<Pick<NetIdPacket, 'uid' | 'version' | 'canon' | 'doc' | 'sha256' | 'chain'>>;

export const isArticle = (p: NetIdPacket): p is ArticlePacket => p.kind !== 'page';

export const PACKET_VERSION = 3;

export const MAGIC = utf8('NID');
export const ENVELOPE_VERSION = 1;
export const SIG_BYTES = 64;
/** Upper bounds a verifier enforces before and after inflating. */
export const MAX_COMPRESSED_BYTES = 64 * 1024;
export const MAX_PACKET_JSON_BYTES = 512 * 1024;

export function randomNonce(bytes = 12): string {
  const b = new Uint8Array(bytes);
  crypto.getRandomValues(b);
  return toBase64Url(b);
}

/**
 * Envelope (SPEC.md §3):
 *   "NID" | 0x01 | issuerLen u8 | issuer | kidLen u8 | kid | deflate-raw(canonical JSON) | sig[64]
 * `sig` is Ed25519 over every byte before it.
 */
export function sealPacket(packet: NetIdPacket, secretKey: Uint8Array): Uint8Array {
  const issuer = utf8(packet.issuer);
  const kid = utf8(packet.kid);
  if (issuer.length < 1 || issuer.length > 255 || kid.length < 1 || kid.length > 255) throw new Error('issuer/kid length');
  const json = utf8(canonicalize(packet));
  const compressed = deflateSync(json, { level: 9 });
  if (compressed.length > MAX_COMPRESSED_BYTES) throw new Error(`packet too large (${compressed.length} bytes compressed)`);
  const signed = concatBytes(MAGIC, Uint8Array.of(ENVELOPE_VERSION, issuer.length), issuer, Uint8Array.of(kid.length), kid, compressed);
  const sig = ed25519.sign(signed, secretKey);
  return concatBytes(signed, sig);
}

export type Envelope = {
  issuer: string;
  kid: string;
  compressed: Uint8Array;
  signed: Uint8Array;
  sig: Uint8Array;
};

/** Splits an envelope without trusting it. Throws on a malformed one. */
export function parseEnvelope(bytes: Uint8Array): Envelope {
  if (bytes.length < MAGIC.length + 4 + SIG_BYTES) throw new Error('envelope too short');
  for (let i = 0; i < MAGIC.length; i++) if (bytes[i] !== MAGIC[i]) throw new Error('not a NetID envelope');
  let pos = MAGIC.length;
  if (bytes[pos++] !== ENVELOPE_VERSION) throw new Error(`unsupported envelope version ${bytes[pos - 1]}`);
  const il = bytes[pos++];
  const issuer = fromUtf8(bytes.subarray(pos, pos + il));
  pos += il;
  const kl = bytes[pos++];
  const kid = fromUtf8(bytes.subarray(pos, pos + kl));
  pos += kl;
  const end = bytes.length - SIG_BYTES;
  if (pos >= end) throw new Error('envelope has no payload');
  if (end - pos > MAX_COMPRESSED_BYTES) throw new Error('payload too large');
  return {
    issuer,
    kid,
    compressed: bytes.subarray(pos, end),
    signed: bytes.subarray(0, end),
    sig: bytes.subarray(end),
  };
}

export function verifyEnvelope(env: Envelope, publicKey: Uint8Array): boolean {
  try {
    return ed25519.verify(env.sig, env.signed, publicKey);
  } catch {
    return false;
  }
}

/** Inflates and parses a VERIFIED envelope's payload, checking its shape. */
export function openPayload(env: Envelope): NetIdPacket {
  const json = inflateSync(env.compressed);
  if (json.length > MAX_PACKET_JSON_BYTES) throw new Error('packet too large');
  const text = fromUtf8(json);
  const p = JSON.parse(text) as NetIdPacket;
  const problems = packetProblems(p);
  if (problems.length) throw new Error(`malformed packet: ${problems.join('; ')}`);
  if (canonicalize(p) !== text) throw new Error('packet JSON is not canonical');
  if (p.issuer !== env.issuer || p.kid !== env.kid) throw new Error('packet issuer/kid differ from the envelope');
  return p;
}

function packetProblems(p: NetIdPacket): string[] {
  const out: string[] = [];
  const isStr = (v: unknown) => typeof v === 'string' && v.length > 0;
  if (!p || typeof p !== 'object') return ['not an object'];
  if (p.netid !== 1 && p.netid !== 2 && p.netid !== 3) out.push('netid not 1, 2 or 3');
  if (p.netid === 3) {
    const s = p.session;
    if (!s || typeof s !== 'object' || !/^[A-Za-z0-9_-]{22}$/.test(String(s.id)) || !/^[A-Za-z0-9_-]{43}$/.test(String(s.pub)) || Object.keys(s).length !== 2) out.push('session (id: 16 bytes, pub: 32 bytes, base64url)');
  } else if (p.session !== undefined) out.push('session needs netid 3');
  if (p.netid === 2 || p.netid === 3) {
    // A company account is a 50-character base58 address; a pending customer has none yet.
    if (p.company !== undefined && (typeof p.company !== 'string' || !/^[1-9A-HJ-NP-Za-km-z]{50}$/.test(p.company))) out.push('company');
    if (p.customer === undefined && p.company === undefined) out.push('company');
    if (typeof p.page !== 'string' || !/^https:\/\/[^/\s]+(\/\S*)?$/.test(p.page) || p.page.length > 2048) out.push('page');
    if (p.customer !== undefined && (!isStr(p.customer) || p.customer !== p.issuer)) out.push('customer (must equal issuer)');
  } else if (p.company !== undefined || p.page !== undefined || p.customer !== undefined || p.kind === 'page') out.push('company/page/customer need netid 2 or 3');
  if (!isStr(p.issuer)) out.push('issuer');
  if (!isStr(p.kid)) out.push('kid');
  if (!['article', 'video', 'audio', 'page'].includes(p.kind)) out.push('kind');
  if (p.kind === 'page') {
    for (const k of ['uid', 'version', 'canon', 'doc', 'sha256', 'chain'] as const) if (p[k] !== undefined) out.push(`${k} not allowed in a page packet`);
  } else {
    if (!isStr(p.uid)) out.push('uid');
    if (!Number.isSafeInteger(p.version) || p.version! < 1) out.push('version');
    if (p.canon !== CANON_V2) out.push('canon');
    if (!p.doc || typeof p.doc.title !== 'string' || typeof p.doc.body !== 'string' || typeof p.doc.published_at !== 'string') out.push('doc');
    if (!/^[0-9a-f]{64}$/.test(p.sha256 ?? '')) out.push('sha256');
    if (!p.chain || !['mainnet', 'testnet'].includes(p.chain.network) || !/^[0-9a-f]{64}$/.test(p.chain.tx_hash ?? '')) out.push('chain');
  }
  if (!Number.isSafeInteger(p.iat)) out.push('iat');
  if (!isStr(p.nonce)) out.push('nonce');
  return out;
}

export function generateKeyPair(): { secretKey: Uint8Array; publicKey: Uint8Array } {
  const secretKey = ed25519.utils.randomSecretKey();
  return { secretKey, publicKey: ed25519.getPublicKey(secretKey) };
}

export function publicKeyOf(secretKey: Uint8Array): Uint8Array {
  return ed25519.getPublicKey(secretKey);
}
